import { useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle, Copy } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Expiry                                                              */
/* ------------------------------------------------------------------ */

// Server limit: OrganizationAPIKeyRevoke.reason is max_length=200.
export const REVOKE_REASON_MAX_LENGTH = 200;

export const NEVER_EXPIRES = "never";
export const DEFAULT_EXPIRY = "30";

export const EXPIRY_OPTIONS = [
  { value: "6", label: "6 days", days: 6 },
  { value: "30", label: "30 days", days: 30 },
  { value: "60", label: "60 days", days: 60 },
  { value: "90", label: "90 days", days: 90 },
  { value: NEVER_EXPIRES, label: "Never expires", days: null }
];

/** Days for the `expires_in_days` field; null means the key never expires. */
export const expiryToDays = (value) =>
  EXPIRY_OPTIONS.find((option) => option.value === value)?.days ?? null;

/** ISO timestamp for the `expires_at` field; null means never expires. */
export const expiryToIso = (value, now = new Date()) => {
  const days = expiryToDays(value);
  if (days === null) return null;
  const at = new Date(now.getTime());
  at.setDate(at.getDate() + days);
  return at.toISOString();
};

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

const parseDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateTime = (iso) => {
  const date = parseDate(iso);
  if (!date) return iso ? String(iso) : "";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

export const formatDate = (iso) => {
  const date = parseDate(iso);
  if (!date) return iso ? String(iso) : "";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

/**
 * How a key is referred to after it is created: its name and when it was
 * created. Never any part of the key itself — the full key is shown once, when
 * it is created, and not again.
 */
export const keyLabel = (item) => {
  if (!item) return "";
  const name =
    typeof item.name === "string" && item.name.trim() ? item.name : "API key";
  const created = item.created_at ? formatDateTime(item.created_at) : "";
  return created ? `${name} (created ${created})` : name;
};

export const lastUsedText = (item) => {
  if (item?.last_used_at)
    return `Last used ${formatDateTime(item.last_used_at)}`;
  // undefined = not known (e.g. only the 409 conflict object is available).
  if (item?.last_used_at === undefined) return "Last used: unknown";
  return "Never used";
};

export const isLive = (item) =>
  item?.status === "active" || item?.status === "retiring";

export const sameName = (a, b) =>
  typeof a === "string" &&
  typeof b === "string" &&
  a.trim().toLowerCase() === b.trim().toLowerCase();

/** True when a mint response says what happened to other keys. */
export const serverReportedEffects = (result) =>
  Array.isArray(result?.revoked_key_ids) || Array.isArray(result?.retiring);

/**
 * Whether the key list may no longer match the server after a failed
 * mutation: the request may have been applied (timeout, network, 5xx), or,
 * for calls on one key, the server says that key is gone or already replaced.
 */
export const listMayBeStale = (res, { keyScoped = false } = {}) => {
  if (!res || res.success) return false;
  if (res.status === null || res.status === undefined) return true;
  if (res.status >= 500) return true;
  return keyScoped && (res.status === 404 || res.status === 409);
};

/** id → key label (name + created), taken from the list before a mutation. */
export const buildKeyLookup = (items) => {
  const lookup = {};
  (items || []).forEach((item) => {
    if (item?.id) lookup[item.id] = keyLabel(item);
  });
  return lookup;
};

/* ------------------------------------------------------------------ */
/* Small components                                                    */
/* ------------------------------------------------------------------ */

const STATUS_STYLES = {
  active: "bg-green-100 text-green-700",
  retiring: "bg-amber-100 text-amber-700",
  expired: "bg-gray-100 text-gray-600",
  revoked: "bg-red-100 text-red-700"
};

const STATUS_LABELS = {
  active: "Active",
  retiring: "Retiring",
  expired: "Expired",
  revoked: "Revoked"
};

export function StatusChip({ status }) {
  const style = STATUS_STYLES[status] || "bg-gray-100 text-gray-600";
  const label = STATUS_LABELS[status] || String(status || "Unknown");
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style}`}
    >
      {label}
    </span>
  );
}

export function ErrorBox({ message, children }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700"
    >
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="break-words">{String(message)}</p>
        {children}
      </div>
    </div>
  );
}

/**
 * Expiry select plus the explicit acknowledgement that "Never expires"
 * requires. Callers block submit until `ack` is true when Never is selected.
 */
export function ExpiryField({
  id,
  value,
  onChange,
  ack,
  onAckChange,
  disabled,
  children
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 mb-1.5"
      >
        Expires after
      </label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {EXPIRY_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {value === NEVER_EXPIRES && (
        <label className="mt-2 flex items-start gap-2 text-xs text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
          <input
            type="checkbox"
            checked={!!ack}
            disabled={disabled}
            onChange={(e) => onAckChange(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            I understand this key never expires and stays valid until someone
            revokes it. I will store it securely.
          </span>
        </label>
      )}
      {children}
    </div>
  );
}

export function CopyButton({ text }) {
  const [state, setState] = useState("idle"); // idle | copied | failed
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("no clipboard");
      await navigator.clipboard.writeText(text);
      setState("copied");
    } catch (e) {
      setState("failed");
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState("idle"), 2500);
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
    >
      {state === "copied" ? (
        <>
          <CheckCircle className="w-3 h-3" />
          Copied!
        </>
      ) : state === "failed" ? (
        <>
          <AlertCircle className="w-3 h-3" />
          Copy failed — select the key and copy it manually
        </>
      ) : (
        <>
          <Copy className="w-3 h-3" />
          Copy key
        </>
      )}
    </button>
  );
}

/**
 * The one-time display of a freshly minted key, plus the effects on other
 * keys exactly as the server reported them (`revoked_key_ids`, `retiring`).
 * Nothing is inferred: without those fields no effect line is printed.
 */
export function NewKeyResult({ result, lookup = {}, footnote, onDone }) {
  const revoked = Array.isArray(result?.revoked_key_ids)
    ? result.revoked_key_ids
    : [];
  const retiring = Array.isArray(result?.retiring) ? result.retiring : [];
  const reported = serverReportedEffects(result);
  const labelFor = (id, fallback) => lookup[id] || fallback || id;

  return (
    <div
      className="bg-green-50 border border-green-200 rounded-lg p-4"
      data-testid="new-key-result"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-semibold text-green-700 flex items-center gap-1">
          <CheckCircle className="w-4 h-4" />
          New API key created
          {result?.name ? (
            <>
              {" "}
              — <span className="font-mono">{result.name}</span>
            </>
          ) : null}
        </span>
        <CopyButton text={result?.raw_key || ""} />
      </div>
      <code className="block text-xs bg-white p-3 rounded border border-green-300 font-mono break-all">
        {result?.raw_key}
      </code>
      <div className="mt-2 text-xs text-gray-700 space-y-1">
        <p>
          <strong className="text-red-600">Important:</strong> Save this key
          securely. It will not be shown again.
        </p>
        <p>
          <strong>Expires:</strong>{" "}
          {result?.expires_at ? formatDateTime(result.expires_at) : "Never"}
        </p>
        {revoked.length > 0 || retiring.length > 0 ? (
          <ul className="space-y-0.5" aria-label="Effects on other keys">
            {revoked.map((id) => (
              <li key={`revoked-${id}`} className="text-red-700">
                Revoked: <span className="font-mono">{labelFor(id)}</span>
              </li>
            ))}
            {retiring.map((entry) => (
              <li key={`retiring-${entry.id}`} className="text-amber-700">
                Retiring at {formatDateTime(entry.retires_at)}:{" "}
                <span className="font-mono">{labelFor(entry.id, "a key")}</span>
              </li>
            ))}
          </ul>
        ) : reported ? (
          <p>Your other keys are unchanged.</p>
        ) : null}
        {footnote ? <p>{footnote}</p> : null}
      </div>
      {onDone && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onDone}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            I've saved the key
          </button>
        </div>
      )}
    </div>
  );
}
